// v1
// Path: frontend/src/components/QuickBooksSync/FieldMappingModal.jsx
// Inputs: token, config, onClose, onSaved
// Outputs: React JSX modal allowing visual assignment, customization, and saving of field mappings between PBS ERP and QuickBooks
// Summary: Interactive field mapping designer that loads source PBS table definitions and destination QuickBooks Online entity fields, allowing users to customize and persist mappings.

import React, { useState, useEffect } from 'react';

export const FieldMappingModal = ({ token, config, onClose, onSaved }) => {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [definitions, setDefinitions] = useState(null);
    const [mappings, setMappings] = useState([]);
    const [newSource, setNewSource] = useState('');
    const [newTarget, setNewTarget] = useState('');

    useEffect(() => {
        const fetchDefinitions = async () => {
            setLoading(true);
            try {
                const res = await fetch(`/api/quickbooks/field-definitions/${config.EntityType}`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                if (res.ok) {
                    const data = await res.json();
                    setDefinitions(data);

                    // Load existing mappings from config or initialize from defaults
                    if (config.FieldMappingsJson) {
                        try {
                            const parsed = typeof config.FieldMappingsJson === 'string' ? JSON.parse(config.FieldMappingsJson) : config.FieldMappingsJson;
                            if (Array.isArray(parsed) && parsed.length > 0) {
                                setMappings(parsed);
                            } else {
                                setMappings(data.defaultMappings || []);
                            }
                        } catch {
                            setMappings(data.defaultMappings || []);
                        }
                    } else {
                        setMappings(data.defaultMappings || []);
                    }
                }
            } catch (err) {
                console.error('[FieldMappingModal] Error loading definitions:', err);
            } finally {
                setLoading(false);
            }
        };

        fetchDefinitions();
    }, [config, token]);

    const handleToggleMapping = (index) => {
        setMappings(prev => {
            const copy = [...prev];
            copy[index] = { ...copy[index], enabled: !copy[index].enabled };
            return copy;
        });
    };

    const handleTargetChange = (index, targetField) => {
        setMappings(prev => {
            const copy = [...prev];
            copy[index] = { ...copy[index], target: targetField };
            return copy;
        });
    };

    const handleRemoveMapping = (index) => {
        setMappings(prev => prev.filter((_, i) => i !== index));
    };

    const handleAddMapping = () => {
        if (!newSource || !newTarget) return;
        setMappings(prev => [
            ...prev,
            { source: newSource, target: newTarget, enabled: true }
        ]);
        setNewSource('');
        setNewTarget('');
    };

    const handleResetDefaults = () => {
        if (definitions?.defaultMappings) {
            setMappings([...definitions.defaultMappings]);
        }
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            const payload = {
                configId: config.ConfigID,
                configName: config.ConfigName,
                accountId: config.AccountID,
                sourceConnPrefix: config.SourceConnPrefix,
                sourceDatabaseName: config.SourceDatabaseName,
                entityType: config.EntityType,
                syncDirection: config.SyncDirection,
                conflictPolicy: config.ConflictPolicy,
                isActive: config.IsActive,
                fieldMappings: mappings
            };

            const res = await fetch('/api/quickbooks/configs', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(payload)
            });

            if (!res.ok) {
                const err = await res.json();
                throw new Error(err.error || 'Failed to save field mappings');
            }

            if (onSaved) onSaved();
            onClose();
        } catch (err) {
            alert(`Error saving mappings: ${err.message}`);
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/75 flex items-center justify-center z-50 p-4">
            <div className="bg-slate-800 border border-slate-700 rounded-xl max-w-3xl w-full max-h-[90vh] flex flex-col shadow-2xl overflow-hidden">
                {/* Header */}
                <div className="p-5 border-b border-slate-700 flex justify-between items-center bg-slate-800/90">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-indigo-600/20 border border-indigo-500/40 flex items-center justify-center text-indigo-400">
                            <span className="material-symbols-outlined">sync_alt</span>
                        </div>
                        <div>
                            <h3 className="text-base font-bold text-white flex items-center gap-2">
                                Field Mappings: <span className="text-blue-400">{config.EntityType}</span>
                            </h3>
                            <p className="text-xs text-slate-400">
                                Source: <code className="text-slate-300">dbo.{definitions?.pbsTable || config.EntityType}</code> &rarr; Target: QuickBooks Online
                            </p>
                        </div>
                    </div>
                    <button onClick={onClose} className="text-slate-400 hover:text-white p-1 rounded">
                        <span className="material-symbols-outlined">close</span>
                    </button>
                </div>

                {/* Content */}
                <div className="p-5 overflow-y-auto flex-1 space-y-5">
                    {loading ? (
                        <div className="p-12 text-center text-slate-400">
                            <span className="material-symbols-outlined animate-spin text-3xl mb-2 text-blue-400">sync</span>
                            <p>Loading schema definitions...</p>
                        </div>
                    ) : (
                        <>
                            {/* Summary bar */}
                            <div className="flex justify-between items-center bg-slate-900/60 p-3 rounded-lg border border-slate-700/60 text-xs">
                                <span className="text-slate-300">
                                    <strong>{mappings.filter(m => m.enabled).length}</strong> active field mappings configured
                                </span>
                                <button
                                    onClick={handleResetDefaults}
                                    className="text-blue-400 hover:underline flex items-center gap-1 font-medium"
                                >
                                    <span className="material-symbols-outlined text-[14px]">restart_alt</span>
                                    Reset to Default Mappings
                                </button>
                            </div>

                            {/* Mapping Table */}
                            <div className="border border-slate-700 rounded-lg overflow-hidden">
                                <table className="w-full text-left text-xs text-slate-300">
                                    <thead className="bg-slate-900/90 text-slate-400 font-semibold border-b border-slate-700">
                                        <tr>
                                            <th className="p-3 w-12 text-center">Sync</th>
                                            <th className="p-3">PBS Source Column</th>
                                            <th className="p-3 w-8 text-center">&rarr;</th>
                                            <th className="p-3">QuickBooks Destination Field</th>
                                            <th className="p-3 w-12 text-center">Action</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-700/60">
                                        {mappings.map((m, idx) => {
                                            const srcDef = definitions?.sourceFields?.find(f => f.name === m.source);
                                            return (
                                                <tr key={idx} className={`hover:bg-slate-750 ${!m.enabled ? 'opacity-50 bg-slate-900/30' : ''}`}>
                                                    <td className="p-3 text-center">
                                                        <input
                                                            type="checkbox"
                                                            checked={m.enabled}
                                                            onChange={() => handleToggleMapping(idx)}
                                                            className="rounded bg-slate-900 border-slate-600 text-blue-600 focus:ring-0 w-4 h-4 cursor-pointer"
                                                        />
                                                    </td>
                                                    <td className="p-3 font-mono">
                                                        <div className="font-semibold text-white">{m.source}</div>
                                                        {srcDef && <div className="text-[10px] text-slate-400 font-sans">{srcDef.label}</div>}
                                                    </td>
                                                    <td className="p-3 text-center text-slate-500 font-bold">&rarr;</td>
                                                    <td className="p-3">
                                                        <select
                                                            value={m.target}
                                                            disabled={!m.enabled}
                                                            onChange={e => handleTargetChange(idx, e.target.value)}
                                                            className="w-full bg-slate-900 border border-slate-700 rounded px-2.5 py-1 text-slate-200 focus:outline-none focus:border-blue-500 font-mono text-xs"
                                                        >
                                                            {definitions?.targetFields?.map(tf => (
                                                                <option key={tf.name} value={tf.name}>
                                                                    {tf.name} ({tf.label})
                                                                </option>
                                                            ))}
                                                        </select>
                                                    </td>
                                                    <td className="p-3 text-center">
                                                        <button
                                                            onClick={() => handleRemoveMapping(idx)}
                                                            className="text-slate-400 hover:text-red-400 p-1 transition-colors"
                                                            title="Remove mapping"
                                                        >
                                                            <span className="material-symbols-outlined text-[16px]">delete</span>
                                                        </button>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>

                            {/* Add Custom Field Mapping */}
                            <div className="bg-slate-900/70 p-3 rounded-lg border border-slate-700/60 space-y-2">
                                <h4 className="text-xs font-semibold text-slate-300 flex items-center gap-1">
                                    <span className="material-symbols-outlined text-[15px] text-blue-400">add_circle</span>
                                    Add Custom Field Mapping
                                </h4>
                                <div className="flex items-center gap-2">
                                    <select
                                        value={newSource}
                                        onChange={e => setNewSource(e.target.value)}
                                        className="flex-1 bg-slate-900 border border-slate-700 rounded px-2.5 py-1.5 text-xs text-white focus:outline-none focus:border-blue-500 font-mono"
                                    >
                                        <option value="">-- Select PBS Source Field --</option>
                                        {definitions?.sourceFields?.map(sf => (
                                            <option key={sf.name} value={sf.name}>
                                                {sf.name} ({sf.label})
                                            </option>
                                        ))}
                                    </select>
                                    <span className="text-slate-500">&rarr;</span>
                                    <select
                                        value={newTarget}
                                        onChange={e => setNewTarget(e.target.value)}
                                        className="flex-1 bg-slate-900 border border-slate-700 rounded px-2.5 py-1.5 text-xs text-white focus:outline-none focus:border-blue-500 font-mono"
                                    >
                                        <option value="">-- Select QuickBooks Field --</option>
                                        {definitions?.targetFields?.map(tf => (
                                            <option key={tf.name} value={tf.name}>
                                                {tf.name} ({tf.label})
                                            </option>
                                        ))}
                                    </select>
                                    <button
                                        type="button"
                                        onClick={handleAddMapping}
                                        disabled={!newSource || !newTarget}
                                        className="px-3 py-1.5 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white rounded text-xs font-semibold transition-colors"
                                    >
                                        Add
                                    </button>
                                </div>
                            </div>
                        </>
                    )}
                </div>

                {/* Footer */}
                <div className="p-4 border-t border-slate-700 flex justify-end gap-3 bg-slate-800/90">
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded text-sm transition-colors"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={handleSave}
                        disabled={saving || loading}
                        className="px-4 py-2 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white rounded text-sm font-semibold transition-colors flex items-center gap-1.5"
                    >
                        {saving ? 'Saving Mappings...' : 'Save Mappings'}
                    </button>
                </div>
            </div>
        </div>
    );
};
